// Firestore-backed public data: product reviews and stats
// Staff can edit these from Admin > Public Site tab

import * as Sentry from '@sentry/react'
import { db, auth } from './firebase'
import { doc, getDoc, setDoc, collection, getDocs, addDoc, updateDoc, deleteDoc } from 'firebase/firestore'

const COLLECTION = 'publicSiteData'
const PROJECT_ID = 'desres-kitchen-v2'
const REST_BASE = `https://firestore.googleapis.com/v1/projects/${PROJECT_ID}/databases/(default)/documents`

// Parse Firestore REST API response fields back to plain JS object
function fromFirestoreFields(fields) {
  if (!fields) return {}
  const obj = {}
  for (const [k, v] of Object.entries(fields)) {
    if ('booleanValue' in v) obj[k] = v.booleanValue
    else if ('doubleValue' in v) obj[k] = Number(v.doubleValue)
    else if ('integerValue' in v) obj[k] = Number(v.integerValue)
    else if ('stringValue' in v) obj[k] = v.stringValue
    else if ('arrayValue' in v) obj[k] = (v.arrayValue.values || []).map(parseFirestoreValue)
    else if ('mapValue' in v) obj[k] = fromFirestoreFields(v.mapValue.fields)
    else if ('nullValue' in v) obj[k] = null
    else obj[k] = v
  }
  return obj
}

function parseFirestoreValue(v) {
  if ('booleanValue' in v) return v.booleanValue
  if ('doubleValue' in v) return Number(v.doubleValue)
  if ('integerValue' in v) return Number(v.integerValue)
  if ('stringValue' in v) return v.stringValue
  if ('arrayValue' in v) return (v.arrayValue.values || []).map(parseFirestoreValue)
  if ('mapValue' in v) return fromFirestoreFields(v.mapValue.fields)
  if ('nullValue' in v) return null
  return v
}

// Read a document via REST — works for both authenticated and public reads
async function restGetDoc(path) {
  const headers = {}
  const token = auth.currentUser ? await auth.currentUser.getIdToken() : null
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(`${REST_BASE}/${path}`, { headers })
  if (res.status === 404) return null
  if (!res.ok) throw new Error(`REST read ${path} failed: ${res.status}`)
  const data = await res.json()
  return fromFirestoreFields(data.fields)
}

// Write a document via REST — requires authentication
async function restSetDoc(path, obj) {
  const token = await auth.currentUser?.getIdToken()
  if (!token) throw new Error('Not authenticated')
  const res = await fetch(`${REST_BASE}/${path}`, {
    method: 'PATCH',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ fields: toFirestoreFields(obj) }),
  })
  if (!res.ok) {
    const err = await res.text()
    throw new Error(`REST write ${path} failed: ${res.status} ${err}`)
  }
}

// --- Reviews & Stats ---
// Shape: { [productKey]: { rating: 4.5, reviewCount: 23, totalSold: 150, featured: false } }

export async function getPublicStats() {
  try {
    const snap = await getDoc(doc(db, COLLECTION, 'productStats'))
    return snap.exists() ? snap.data().items || {} : {}
  } catch {
    return {}
  }
}

export async function savePublicStats(items) {
  try {
    await setDoc(doc(db, COLLECTION, 'productStats'), { items, updatedAt: new Date().toISOString() })
  } catch (e) {
    console.error('Failed to save public stats:', e)
    throw e
  }
}

// Helper to generate a stable key from branch + product name
export function productKey(branch, name) {
  return `${branch}__${name}`.toLowerCase().replace(/[^a-z0-9_]/g, '_')
}

// --- WhatsApp config ---
export async function getWhatsAppConfig() {
  try {
    const snap = await getDoc(doc(db, COLLECTION, 'whatsappConfig'))
    return snap.exists() ? snap.data() : { kitchen: '', yawehs: '' }
  } catch {
    return { kitchen: '', yawehs: '' }
  }
}

export async function saveWhatsAppConfig(config) {
  try {
    await setDoc(doc(db, COLLECTION, 'whatsappConfig'), { ...config, updatedAt: new Date().toISOString() })
  } catch (err) {
    console.warn('[publicData] saveWhatsAppConfig failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'publicData', fn: 'saveWhatsAppConfig' } })
    throw err
  }
}

// --- Careers / Job Listings ---
const CAREERS_COLLECTION = 'careers'

export async function getCareers() {
  try {
    const snap = await getDocs(collection(db, CAREERS_COLLECTION))
    return snap.docs.map(d => ({ id: d.id, ...d.data() }))
  } catch {
    return []
  }
}

export async function addCareer(job) {
  try {
    const ref = await addDoc(collection(db, CAREERS_COLLECTION), {
      ...job,
      createdAt: new Date().toISOString(),
    })
    return ref.id
  } catch (err) {
    console.warn('[publicData] addCareer failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'publicData', fn: 'addCareer' } })
    throw err
  }
}

export async function updateCareer(id, data) {
  try {
    await updateDoc(doc(db, CAREERS_COLLECTION, id), { ...data, updatedAt: new Date().toISOString() })
  } catch (err) {
    console.warn('[publicData] updateCareer failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'publicData', fn: 'updateCareer', id } })
    throw err
  }
}

export async function deleteCareer(id) {
  try {
    await deleteDoc(doc(db, CAREERS_COLLECTION, id))
  } catch (err) {
    console.warn('[publicData] deleteCareer failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'publicData', fn: 'deleteCareer', id } })
    throw err
  }
}

// --- Ordering Permissions ---
// Shape: { 'desres__meals': true, 'desres__bakes': false, 'yawehs__sweets': true, ... }
// Key format: businessKey__categoryKey — all default to true if missing
export async function getOrderingPermissions() {
  try {
    const data = await restGetDoc('publicSiteData/orderingPermissions')
    return data || {}
  } catch (err) {
    console.error('[getOrderingPermissions] REST read failed:', err)
    return {}
  }
}

// Convert JS object to Firestore REST API field format
function toFirestoreFields(obj) {
  const fields = {}
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === 'boolean') fields[k] = { booleanValue: v }
    else if (typeof v === 'number') fields[k] = { doubleValue: v }
    else fields[k] = { stringValue: String(v) }
  }
  return fields
}

export async function saveOrderingPermissions(permissions) {
  const { updatedAt, ...perms } = permissions
  const data = { ...perms, updatedAt: new Date().toISOString() }
  const start = Date.now()
  await restSetDoc('publicSiteData/orderingPermissions', data)
  console.log('[savePerms] done via REST in', Date.now() - start, 'ms')
}

// Diagnostic: call window.__testFirestoreWrite() from browser console
if (typeof window !== 'undefined') {
  window.__testFirestoreWrite = async () => {
    try {
      console.log('Auth user:', auth.currentUser?.email || 'NOT AUTHENTICATED')
      const token = await auth.currentUser?.getIdToken()
      const url = 'https://firestore.googleapis.com/v1/projects/desres-kitchen-v2/databases/(default)/documents/publicSiteData/_test'
      console.log('Writing test doc via REST...')
      const start = Date.now()
      const res = await fetch(url, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields: { test: { booleanValue: true } } }),
      })
      console.log(`REST write: ${res.status} in ${Date.now() - start}ms`)
      if (res.ok) {
        await fetch(url, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } })
        console.log('Cleaned up')
      } else {
        console.error('Failed:', await res.text())
      }
    } catch (err) {
      console.error('Test write FAILED:', err)
    }
  }
}

// --- Invoice Counter & History ---
const INVOICES_COLLECTION = 'invoices'

export async function getNextInvoiceNumber() {
  const ref = doc(db, COLLECTION, 'invoiceCounter')
  const snap = await getDoc(ref)
  const current = snap.exists() ? (snap.data().nextNumber || 1001) : 1001
  await setDoc(ref, { nextNumber: current + 1 })
  return current
}

export async function saveInvoice(invoice) {
  const ref = await addDoc(collection(db, INVOICES_COLLECTION), {
    ...invoice,
    createdAt: new Date().toISOString(),
  })
  return ref.id
}

export async function getInvoices() {
  try {
    const snap = await getDocs(collection(db, INVOICES_COLLECTION))
    return snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
  } catch {
    return []
  }
}
